import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { loadGoogleMaps, API_KEY } from "@/lib/google-maps-loader";

export interface StructuredAddress {
  street: string; // e.g. "Musterstraße 1"
  postalCode: string; // e.g. "10115"
  city: string; // e.g. "Berlin"
  country: string; // e.g. "DE"
}

/**
 * Compose a display string from structured address parts.
 * Used to populate the typeahead input when loading existing data.
 */
export function composeAddressDisplay(addr: StructuredAddress): string {
  const parts: string[] = [];
  if (addr.street) parts.push(addr.street);
  if (addr.postalCode || addr.city) {
    parts.push([addr.postalCode, addr.city].filter(Boolean).join(" "));
  }
  return parts.join(", ");
}

interface AddressAutocompleteProps {
  /** The raw display string shown in the input */
  value: string;
  onChange: (value: string) => void;
  onStructuredChange?: (address: StructuredAddress) => void;
  /** Called with lat/lng when a place is selected */
  onCoordinatesChange?: (lat: number, lng: number) => void;
  /** Structured address for showing the parsed summary below the input */
  structuredValue?: StructuredAddress;
  /** Google Places primary types to include in autocomplete suggestions.
   *  Defaults to ["street_address"]. Use ["locality", "administrative_area_level_3"]
   *  for city-level search. */
  types?: string[];
  placeholder?: string;
  id?: string;
  required?: boolean;
  className?: string;
}

/**
 * Parse new Places API `addressComponents` into a `StructuredAddress`.
 * Exported as a pure function so it can be unit-tested independently.
 *
 * German street format: "Straßenname Hausnummer" (route first, then number).
 */
export function parseAddressComponents(
  components: google.maps.places.AddressComponent[],
): StructuredAddress {
  let route = "";
  let streetNumber = "";
  let postalCode = "";
  let city = "";
  let country = "";

  for (const component of components) {
    const types = component.types;
    if (types.includes("route")) {
      route = component.longText || "";
    } else if (types.includes("street_number")) {
      streetNumber = component.longText || "";
    } else if (types.includes("postal_code")) {
      postalCode = component.longText || "";
    } else if (types.includes("locality")) {
      city = component.longText || "";
    } else if (types.includes("country")) {
      country = component.shortText || ""; // ISO 3166-1 alpha-2, e.g. "DE"
    }
  }

  // German convention: route before street number ("Musterstraße 1")
  const street = streetNumber ? `${route} ${streetNumber}`.trim() : route;

  return { street, postalCode, city, country };
}

interface Prediction {
  placeId: string;
  description: string;
  placePrediction: google.maps.places.PlacePrediction;
}

export function AddressAutocomplete({
  value,
  onChange,
  onStructuredChange,
  onCoordinatesChange,
  structuredValue,
  types,
  placeholder,
  id,
  required,
  className,
}: AddressAutocompleteProps) {
  // API_KEY is a module-level constant — safe to branch here without hooks concerns
  if (!API_KEY) {
    return (
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={className}
      />
    );
  }

  return (
    <AutocompleteInput
      value={value}
      onChange={onChange}
      onStructuredChange={onStructuredChange}
      onCoordinatesChange={onCoordinatesChange}
      structuredValue={structuredValue}
      types={types}
      placeholder={placeholder}
      id={id}
      required={required}
      className={className}
    />
  );
}

function AutocompleteInput({
  value,
  onChange,
  onStructuredChange,
  onCoordinatesChange,
  structuredValue,
  types,
  placeholder,
  id,
  required,
  className,
}: AddressAutocompleteProps) {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [ready, setReady] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        sessionToken.current =
          new google.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((err) => {
        console.error("Google Maps failed to load:", err);
      });
  }, []);

  // Clear debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchPredictions = useCallback(
    (input: string) => {
      if (!ready || input.length < 3) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(async () => {
        try {
          const { suggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              {
                input,
                includedRegionCodes: ["de"],
                includedPrimaryTypes: types ?? ["street_address"],
                sessionToken: sessionToken.current ?? undefined,
              },
            );

          if (suggestions && suggestions.length > 0) {
            setPredictions(
              suggestions
                .filter((s) => s.placePrediction)
                .map((s) => ({
                  placeId: s.placePrediction!.placeId,
                  description: s.placePrediction!.text.text,
                  placePrediction: s.placePrediction!,
                })),
            );
            setIsOpen(true);
            setActiveIndex(-1);
          } else {
            setPredictions([]);
            setIsOpen(false);
          }
        } catch (err) {
          console.error("Autocomplete fetch failed:", err);
          setPredictions([]);
          setIsOpen(false);
        }
      }, 300);
    },
    [ready],
  );

  async function selectPlace(prediction: Prediction) {
    try {
      const place = prediction.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["formattedAddress", "addressComponents", "location"],
      });

      const structured = place.addressComponents
        ? parseAddressComponents(place.addressComponents)
        : null;

      // Set the input to a clean composed string (not Google's verbose formattedAddress)
      if (structured) {
        onChange(composeAddressDisplay(structured));
      } else if (place.formattedAddress) {
        onChange(place.formattedAddress);
      }

      // Emit structured fields when the caller opted in
      if (onStructuredChange && structured) {
        onStructuredChange(structured);
      }

      // Emit coordinates when the caller opted in
      if (onCoordinatesChange && place.location) {
        onCoordinatesChange(place.location.lat(), place.location.lng());
      }

      // Rotate session token after a place selection (per Google billing docs)
      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
    } catch (err) {
      console.error("Place details fetch failed:", err);
    }

    setIsOpen(false);
    setPredictions([]);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onChange(val);
    fetchPredictions(val);

    // When the user manually edits the input, clear structured fields so
    // validation correctly marks the address as incomplete until a new
    // suggestion is selected. This prevents stale structured data.
    if (onStructuredChange) {
      onStructuredChange({ street: "", postalCode: "", city: "", country: "" });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1,
        );
        break;
      case "Enter":
        if (activeIndex >= 0) {
          e.preventDefault();
          selectPlace(predictions[activeIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  }

  // Build summary from structured value — reuse composeAddressDisplay, append country
  const summaryText = structuredValue
    ? [composeAddressDisplay(structuredValue), structuredValue.country]
        .filter(Boolean)
        .join(" \u00B7 ")
    : "";

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (predictions.length > 0) setIsOpen(true);
        }}
        placeholder={placeholder}
        required={required}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={id ? `${id}-listbox` : undefined}
        aria-activedescendant={
          activeIndex >= 0 && id ? `${id}-option-${activeIndex}` : undefined
        }
      />
      {isOpen && predictions.length > 0 && (
        <ul
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className={cn(
            "absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md",
            "max-h-60 overflow-auto py-1",
          )}
        >
          {predictions.map((prediction, index) => (
            <li
              key={prediction.placeId}
              id={id ? `${id}-option-${index}` : undefined}
              role="option"
              aria-selected={index === activeIndex}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm",
                index === activeIndex
                  ? "bg-muted text-foreground"
                  : "hover:bg-muted/50",
              )}
              onMouseDown={(e) => {
                e.preventDefault(); // prevent blur
                selectPlace(prediction);
              }}
            >
              {prediction.description}
            </li>
          ))}
        </ul>
      )}
      {summaryText && (
        <p className="mt-1.5 text-xs text-muted-foreground">{summaryText}</p>
      )}
    </div>
  );
}
